// 이슈 트래킹 (distributor-desk) — Design §2.6, FR-15/FR-16.
// distributor_issues 를 severity/status 뱃지와 함께 보드/피드로 표시하고, 이슈를
// 펼치면 조치 로그(distributor_issue_events, append-only)를 타임라인으로 본다.
//
// 라이프사이클: open → in_progress → resolved → closed (되돌리기도 허용). 전이는
// 전부 사용자 클릭이며 자동 전이 없음. 상태 변경 시 issues.update(status,resolved_at)
// + issue_events.addEvent(action:'status_change') 로 조치 로그를 append 한다.
// 코멘트도 addEvent(action:'comment') 로 수동 기록. severity/status 는 rule_findings
// 와 다른 비즈니스 체계다(위 enum 그대로).
//
// 데이터 소유 규칙: countries/partners/contracts 는 읽기 전용 참조(useDistributorData).
// 이 페이지가 변경하는 issues 는 자체 state 로 로드하고 mutate 성공 후 재로드한다.
import { useCallback, useEffect, useMemo, useState } from "react";
import { isSupabaseConfigured } from "@/lib/supabase";
import {
  distributorIssuesRepo,
  type NewDistributorIssue,
  type NewDistributorIssueEvent,
} from "@/lib/db";
import type {
  ContractRow,
  Country,
  DistributorIssue,
  DistributorIssueEvent,
  DistributorIssueEventAction,
  DistributorIssueSeverity,
  DistributorIssueStatus,
  DistributorIssueType,
  Partner,
} from "@/types";
import {
  Badge,
  Btn,
  Card,
  cx,
  Disclaimer,
  EmptyState,
  Field,
  IconChevron,
  IconFlag,
  Input,
  PageHeader,
  SectionLabel,
  Select,
  StatCard,
  Textarea,
  type Tone,
} from "./ui";
import { useDistributorData } from "./DistributorDeskLayout";

// ── labels & tone palette ────────────────────────────────────────────────────
const TYPE_LABEL: Record<DistributorIssueType, string> = {
  claim: "클레임",
  return: "반품",
  registration_delay: "인허가 지연",
  exclusivity_dispute: "독점권 분쟁",
  payment: "대금",
  logistics: "물류",
  other: "기타",
};
const TYPE_OPTS: DistributorIssueType[] = [
  "claim",
  "return",
  "registration_delay",
  "exclusivity_dispute",
  "payment",
  "logistics",
  "other",
];

const SEVERITY_LABEL: Record<DistributorIssueSeverity, string> = {
  critical: "치명",
  high: "높음",
  medium: "보통",
  low: "낮음",
};
// severity: critical→danger, high→warn, medium/low→neutral.
const SEVERITY_TONE: Record<DistributorIssueSeverity, Tone> = {
  critical: "danger",
  high: "warn",
  medium: "neutral",
  low: "neutral",
};
const SEVERITY_OPTS: DistributorIssueSeverity[] = ["critical", "high", "medium", "low"];
const SEVERITY_WEIGHT: Record<DistributorIssueSeverity, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

const STATUS_LABEL: Record<DistributorIssueStatus, string> = {
  open: "접수",
  in_progress: "처리중",
  resolved: "해결",
  closed: "종료",
};
// status: open→warn, in_progress→accent, resolved→success, closed→neutral.
const STATUS_TONE: Record<DistributorIssueStatus, Tone> = {
  open: "warn",
  in_progress: "accent",
  resolved: "success",
  closed: "neutral",
};
// 라이프사이클 순서 (되돌리기 = 아무 상태나 클릭 허용).
const STATUS_FLOW: DistributorIssueStatus[] = ["open", "in_progress", "resolved", "closed"];

const ACTION_LABEL: Record<DistributorIssueEventAction, string> = {
  comment: "코멘트",
  status_change: "상태 변경",
  assign: "배정",
  attach: "첨부",
};

const CONTRACT_STATUS_LABEL: Record<string, string> = {
  active: "활성",
  done: "완료",
  cancelled: "취소",
};

const today = (): string => new Date().toISOString().slice(0, 10);
const dayOnly = (s: string | null | undefined): string => (s ? s.slice(0, 10) : "—");
const stampMin = (s: string | null | undefined): string =>
  s ? s.slice(0, 16).replace("T", " ") : "—";
const isOpenStatus = (s: DistributorIssueStatus): boolean =>
  s === "open" || s === "in_progress";
const isOverdue = (i: DistributorIssue): boolean =>
  Boolean(i.due_date) && isOpenStatus(i.status) && (i.due_date as string) < today();

/** 상태 전이 시 contracts 패치 — resolved 진입 시 resolved_at=today, 재개 시 clear. */
function statusPatch(next: DistributorIssueStatus): Partial<NewDistributorIssue> {
  if (next === "resolved") return { status: next, resolved_at: today() };
  if (next === "open" || next === "in_progress") return { status: next, resolved_at: null };
  return { status: next }; // closed: 기존 resolved_at 보존
}

// ── create draft ─────────────────────────────────────────────────────────────
interface IssueDraft {
  title: string;
  issue_type: DistributorIssueType;
  severity: DistributorIssueSeverity;
  country_id: string; // "" = none
  partner_id: string;
  contract_id: string;
  owner: string;
  due_date: string;
  resolution_note: string;
}
const EMPTY_DRAFT: IssueDraft = {
  title: "",
  issue_type: "claim",
  severity: "medium",
  country_id: "",
  partner_id: "",
  contract_id: "",
  owner: "",
  due_date: "",
  resolution_note: "",
};

export function IssuesPage(): JSX.Element {
  const d = useDistributorData();
  const { countries, partners, contracts } = d;
  const configured = isSupabaseConfigured();

  // 이 페이지가 변경하는 모듈 데이터 — 자체 로드/재로드.
  const [issues, setIssues] = useState<DistributorIssue[]>([]);
  const [loading, setLoading] = useState(true);

  // create panel
  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState<IssueDraft>(EMPTY_DRAFT);
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState<string | null>(null);

  // filters
  const [filter, setFilter] = useState<"all" | DistributorIssueStatus>("all");

  // expanded issue → events cache + comment/edit state
  const [openIssue, setOpenIssue] = useState<string | null>(null);
  const [events, setEvents] = useState<Record<string, DistributorIssueEvent[]>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const rows = await distributorIssuesRepo.list().catch(() => [] as DistributorIssue[]);
    setIssues(rows);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!configured) {
        if (alive) setLoading(false);
        return;
      }
      await reload();
      if (alive) setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [configured, reload]);

  const loadEvents = useCallback(async (issueId: string) => {
    try {
      const ev = await distributorIssuesRepo.events(issueId);
      setEvents((prev) => ({ ...prev, [issueId]: ev }));
    } catch {
      setEvents((prev) => ({ ...prev, [issueId]: [] }));
    }
  }, []);

  // ── reference label helpers ────────────────────────────────────────────────
  const countryLabel = useCallback(
    (id: string | null | undefined): string => {
      if (!id) return "국가 미지정";
      const c = countries.find((x) => x.id === id);
      return c ? c.name_ko ?? c.name_en : id;
    },
    [countries],
  );
  const partnerName = useCallback(
    (id: string | null | undefined): string => {
      if (!id) return "총판 미지정";
      return partners.find((p) => p.id === id)?.company_name ?? "(미상 총판)";
    },
    [partners],
  );
  const contractName = useCallback(
    (id: string | null | undefined): string | null => {
      if (!id) return null;
      return contracts.find((c) => c.id === id)?.name ?? "(삭제된 계약)";
    },
    [contracts],
  );

  // ── derived board ──────────────────────────────────────────────────────────
  const counts = useMemo(() => {
    const c: Record<"all" | DistributorIssueStatus, number> = {
      all: issues.length,
      open: 0,
      in_progress: 0,
      resolved: 0,
      closed: 0,
    };
    for (const i of issues) c[i.status] += 1;
    return c;
  }, [issues]);

  const kpi = useMemo(() => {
    const openCnt = issues.filter((i) => isOpenStatus(i.status)).length;
    const urgent = issues.filter(
      (i) => isOpenStatus(i.status) && (i.severity === "critical" || i.severity === "high"),
    ).length;
    const overdue = issues.filter(isOverdue).length;
    return { openCnt, urgent, overdue };
  }, [issues]);

  const board = useMemo(() => {
    const filtered = filter === "all" ? issues : issues.filter((i) => i.status === filter);
    // 미해결 우선 → severity 가중치 → 접수일 내림차순.
    return [...filtered].sort((a, b) => {
      const oa = isOpenStatus(a.status) ? 0 : 1;
      const ob = isOpenStatus(b.status) ? 0 : 1;
      if (oa !== ob) return oa - ob;
      const sw = SEVERITY_WEIGHT[a.severity] - SEVERITY_WEIGHT[b.severity];
      if (sw !== 0) return sw;
      return (b.opened_at ?? "").localeCompare(a.opened_at ?? "");
    });
  }, [issues, filter]);

  // ── mutations (all manual; reload after success) ───────────────────────────
  async function createIssue(): Promise<void> {
    const title = draft.title.trim();
    if (!title) return;
    setCreating(true);
    setCreateErr(null);
    try {
      const row: NewDistributorIssue = {
        title,
        issue_type: draft.issue_type,
        severity: draft.severity,
        country_id: draft.country_id || null,
        partner_id: draft.partner_id || null,
        contract_id: draft.contract_id || null,
        status: "open",
        owner: draft.owner.trim() || null,
        due_date: draft.due_date || null,
        opened_at: today(),
        resolved_at: null,
        resolution_note: draft.resolution_note.trim(),
      };
      await distributorIssuesRepo.create(row);
      setDraft(EMPTY_DRAFT);
      setShowCreate(false);
      await reload();
    } catch (e) {
      setCreateErr("이슈 생성 실패: " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setCreating(false);
    }
  }

  async function changeStatus(
    issue: DistributorIssue,
    next: DistributorIssueStatus,
  ): Promise<void> {
    if (issue.status === next) return;
    setBusyId(issue.id);
    try {
      await distributorIssuesRepo.update(issue.id, statusPatch(next));
      // 조치 로그 append (append-only) — 상태 전이 기록.
      const ev: NewDistributorIssueEvent = {
        issue_id: issue.id,
        actor: "mmakid",
        action: "status_change",
        from_status: issue.status,
        to_status: next,
        body: "",
      };
      await distributorIssuesRepo.addEvent(ev);
      await reload();
      if (openIssue === issue.id) await loadEvents(issue.id);
    } catch (e) {
      window.alert("상태 변경 실패: " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setBusyId(null);
    }
  }

  async function toggleExpand(issueId: string): Promise<void> {
    if (openIssue === issueId) {
      setOpenIssue(null);
      return;
    }
    setOpenIssue(issueId);
    if (!events[issueId]) await loadEvents(issueId);
  }

  // ── render ─────────────────────────────────────────────────────────────────
  if (!configured) {
    return (
      <div className="space-y-5">
        <Header showCreate={false} onToggle={() => {}} disabled />
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-3 text-sm text-amber-800">
          Supabase 미설정 — 이슈 등록·조회가 비활성화됩니다. <code>.env</code> 설정 후 이용하십시오.
        </div>
        <Disclaimer>
          본 도구는 사전 정의된 항목만 취합·표시합니다. 최종 판단·확인은 담당자 책임입니다. 계약가·마진 등
          대외비 데이터는 CF Access(SSO) 게이팅 이후 적재하십시오.
        </Disclaimer>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <Header
        showCreate={showCreate}
        onToggle={() => {
          setDraft(EMPTY_DRAFT);
          setCreateErr(null);
          setShowCreate((v) => !v);
        }}
        disabled={false}
      />

      {/* KPI 타일 */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="전체 이슈" value={counts.all} unit="건" />
        <StatCard label="미해결" value={kpi.openCnt} unit="건" tone={kpi.openCnt > 0 ? "warn" : "neutral"} />
        <StatCard
          label="긴급 미해결"
          value={kpi.urgent}
          unit="건"
          tone={kpi.urgent > 0 ? "danger" : "neutral"}
        />
        <StatCard
          label="기한 초과"
          value={kpi.overdue}
          unit="건"
          tone={kpi.overdue > 0 ? "danger" : "neutral"}
        />
      </div>

      {/* create panel */}
      {showCreate && (
        <Card className="space-y-3 p-4">
          <SectionLabel>새 이슈 등록</SectionLabel>
          <IssueFormFields
            draft={draft}
            onChange={setDraft}
            countries={countries}
            partners={partners}
            contracts={contracts}
          />
          {createErr && (
            <p className="rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-xs text-rose-700">
              {createErr}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Btn
              variant="primary"
              onClick={() => void createIssue()}
              disabled={creating || !draft.title.trim()}
            >
              {creating ? "등록 중…" : "이슈 등록"}
            </Btn>
            <Btn variant="ghost" onClick={() => setShowCreate(false)} disabled={creating}>
              취소
            </Btn>
            <span className="text-xs text-slate-400">
              등록 후 접수(open) 상태로 시작합니다. 상태 전이는 담당자가 수동으로 진행합니다.
            </span>
          </div>
        </Card>
      )}

      {/* status filter chips */}
      <div className="flex flex-wrap gap-1.5">
        {(["all", ...STATUS_FLOW] as const).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setFilter(k)}
            className={cx(
              "inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500",
              filter === k
                ? "bg-indigo-600 text-white ring-indigo-600"
                : "bg-white text-slate-500 ring-slate-200 hover:bg-slate-50",
            )}
          >
            {k === "all" ? "전체" : STATUS_LABEL[k]}
            <span className="tnum opacity-70">({counts[k]})</span>
          </button>
        ))}
      </div>

      {/* board */}
      {loading ? (
        <Card className="px-4 py-16">
          <div className="text-center text-sm text-slate-400">불러오는 중…</div>
        </Card>
      ) : board.length === 0 ? (
        <EmptyState icon={<IconFlag className="h-8 w-8" />} title="표시할 이슈가 없습니다">
          상단 [+ 새 이슈] 로 클레임·반품·인허가 지연·독점권 분쟁 등을 등록하면 이 보드에 취합됩니다.
        </EmptyState>
      ) : (
        <div className="space-y-3">
          {board.map((i) => (
            <IssueCard
              key={i.id}
              issue={i}
              expanded={openIssue === i.id}
              busy={busyId === i.id}
              events={events[i.id]}
              countryLabel={countryLabel(i.country_id)}
              partnerLabel={partnerName(i.partner_id)}
              contractLabel={contractName(i.contract_id)}
              onToggle={() => void toggleExpand(i.id)}
              onChangeStatus={(next) => void changeStatus(i, next)}
              onEventsChanged={() => void loadEvents(i.id)}
              onSaved={reload}
            />
          ))}
        </div>
      )}

      <Disclaimer>
        본 도구는 사전 정의된 항목만 취합·표시합니다. 최종 판단·확인은 담당자 책임입니다. 계약가·마진 등
        대외비 데이터는 CF Access(SSO) 게이팅 이후 적재하십시오.
      </Disclaimer>
    </div>
  );
}

// ── 이슈 카드 (헤더 + 상태전이 + 펼침: 편집 + 조치로그) ───────────────────────
function IssueCard({
  issue,
  expanded,
  busy,
  events,
  countryLabel,
  partnerLabel,
  contractLabel,
  onToggle,
  onChangeStatus,
  onEventsChanged,
  onSaved,
}: {
  issue: DistributorIssue;
  expanded: boolean;
  busy: boolean;
  events: DistributorIssueEvent[] | undefined;
  countryLabel: string;
  partnerLabel: string;
  contractLabel: string | null;
  onToggle: () => void;
  onChangeStatus: (next: DistributorIssueStatus) => void;
  onEventsChanged: () => void;
  onSaved: () => Promise<void>;
}): JSX.Element {
  const overdue = isOverdue(issue);
  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-start justify-between gap-2 p-3.5">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge tone={SEVERITY_TONE[issue.severity]}>{SEVERITY_LABEL[issue.severity]}</Badge>
            <Badge tone={STATUS_TONE[issue.status]}>{STATUS_LABEL[issue.status]}</Badge>
            <Badge tone="neutral">{TYPE_LABEL[issue.issue_type]}</Badge>
            <h3 className="text-sm font-semibold text-slate-800">{issue.title}</h3>
            {overdue && <Badge tone="danger">기한 초과</Badge>}
          </div>
          <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-400">
            <span>{countryLabel}</span>
            <span>{partnerLabel}</span>
            {contractLabel && <span>계약 · {contractLabel}</span>}
            {issue.owner && <span>담당 · {issue.owner}</span>}
            <span className={overdue ? "text-rose-600" : ""}>기한 {dayOnly(issue.due_date)}</span>
            <span>접수 {dayOnly(issue.opened_at)}</span>
            {issue.resolved_at && <span className="text-emerald-600">해결 {dayOnly(issue.resolved_at)}</span>}
          </div>
        </div>
      </div>

      {/* 상태 전이 (되돌리기 허용 — 아무 상태나 클릭) */}
      <div className="flex flex-wrap items-center gap-1 border-t border-slate-100 px-3.5 py-2.5">
        <span className="mr-1 text-[11px] font-medium text-slate-400">상태 전이</span>
        {STATUS_FLOW.map((s) => {
          const active = issue.status === s;
          return (
            <button
              key={s}
              type="button"
              onClick={() => onChangeStatus(s)}
              disabled={busy || active}
              className={cx(
                "rounded-md px-2.5 py-1 text-[12px] font-medium transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 disabled:cursor-not-allowed",
                active
                  ? "bg-indigo-50 text-indigo-700 ring-1 ring-inset ring-indigo-600/20"
                  : "bg-white text-slate-500 ring-1 ring-inset ring-slate-200 hover:bg-slate-50 disabled:opacity-50",
              )}
            >
              {STATUS_LABEL[s]}
            </button>
          );
        })}
        <span className="ml-1 text-[11px] text-slate-400">전이는 조치 로그에 자동 기록됩니다.</span>
      </div>

      {/* 펼침: 편집 + 조치 로그 */}
      <div className="border-t border-slate-100 px-3.5 py-2.5">
        <button
          type="button"
          className="inline-flex items-center gap-1 border-0 bg-transparent p-0 text-xs font-medium text-slate-500 transition hover:text-slate-800"
          onClick={onToggle}
        >
          <IconChevron className={cx("h-3.5 w-3.5 transition-transform", expanded && "rotate-90")} />
          상세 · 조치 로그
          {events ? ` (${events.length})` : ""}
        </button>

        {expanded && (
          <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
            <IssueEditPanel issue={issue} onSaved={onSaved} />
            <IssueEventsPanel
              issueId={issue.id}
              events={events}
              onEventsChanged={onEventsChanged}
            />
          </div>
        )}
      </div>
    </Card>
  );
}

// ── 이슈 편집 패널 (severity/owner/due_date/resolution_note) ──────────────────
function IssueEditPanel({
  issue,
  onSaved,
}: {
  issue: DistributorIssue;
  onSaved: () => Promise<void>;
}): JSX.Element {
  const [severity, setSeverity] = useState<DistributorIssueSeverity>(issue.severity);
  const [owner, setOwner] = useState(issue.owner ?? "");
  const [due, setDue] = useState(issue.due_date ?? "");
  const [note, setNote] = useState(issue.resolution_note ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const dirty =
    severity !== issue.severity ||
    (owner.trim() || "") !== (issue.owner ?? "") ||
    (due || "") !== (issue.due_date ?? "") ||
    (note.trim() || "") !== (issue.resolution_note ?? "");

  const save = async (): Promise<void> => {
    setErr(null);
    setBusy(true);
    try {
      await distributorIssuesRepo.update(issue.id, {
        severity,
        owner: owner.trim() || null,
        due_date: due || null,
        resolution_note: note.trim(),
      });
      await onSaved();
    } catch (e) {
      setErr("저장 실패: " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="space-y-3 p-3.5">
      <SectionLabel>이슈 편집</SectionLabel>
      <div className="grid grid-cols-2 gap-3">
        <Field label="심각도">
          <Select
            value={severity}
            onChange={(e) => setSeverity(e.target.value as DistributorIssueSeverity)}
          >
            {SEVERITY_OPTS.map((s) => (
              <option key={s} value={s}>
                {SEVERITY_LABEL[s]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="담당자">
          <Input value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="담당자명" />
        </Field>
        <Field label="기한">
          <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} />
        </Field>
        <Field label="해결/조치 메모" className="col-span-2">
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="해결 경위·조치 내용 (해결 시 기록 권장)"
          />
        </Field>
      </div>
      {err && (
        <p className="rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-xs text-rose-700">
          {err}
        </p>
      )}
      <Btn variant="primary" onClick={() => void save()} disabled={busy || !dirty}>
        {busy ? "저장 중…" : "변경 저장"}
      </Btn>
    </Card>
  );
}

// ── 조치 로그 패널 (타임라인 + 코멘트 추가) ──────────────────────────────────
function IssueEventsPanel({
  issueId,
  events,
  onEventsChanged,
}: {
  issueId: string;
  events: DistributorIssueEvent[] | undefined;
  onEventsChanged: () => void;
}): JSX.Element {
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const addComment = async (): Promise<void> => {
    const body = comment.trim();
    if (!body) return;
    setErr(null);
    setBusy(true);
    try {
      const ev: NewDistributorIssueEvent = {
        issue_id: issueId,
        actor: "mmakid",
        action: "comment",
        from_status: null,
        to_status: null,
        body,
      };
      await distributorIssuesRepo.addEvent(ev);
      setComment("");
      onEventsChanged();
    } catch (e) {
      setErr("코멘트 추가 실패: " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="space-y-3 p-3.5">
      <SectionLabel>조치 로그 · append-only</SectionLabel>

      {events == null ? (
        <p className="text-xs text-slate-400">불러오는 중…</p>
      ) : events.length === 0 ? (
        <p className="text-xs text-slate-400">기록된 조치가 없습니다. 아래에서 코멘트를 남기세요.</p>
      ) : (
        <ol className="space-y-2">
          {events.map((ev) => (
            <li
              key={ev.id}
              className="rounded-lg border border-slate-100 bg-slate-50/60 px-2.5 py-2 text-xs text-slate-700"
            >
              <div className="flex flex-wrap items-center gap-1.5">
                <Badge tone={ev.action === "status_change" ? "accent" : "neutral"}>
                  {ACTION_LABEL[ev.action]}
                </Badge>
                {ev.action === "status_change" && ev.from_status && ev.to_status && (
                  <span className="inline-flex items-center gap-1 text-slate-500">
                    {STATUS_LABEL[ev.from_status]}
                    <IconChevron className="h-3 w-3 text-slate-300" />
                    {STATUS_LABEL[ev.to_status]}
                  </span>
                )}
                <span className="tnum ml-auto text-[10px] text-slate-400">
                  {ev.actor} · {stampMin(ev.event_at)}
                </span>
              </div>
              {ev.body.trim() && (
                <p className="mt-1 whitespace-pre-wrap leading-relaxed text-slate-600">{ev.body}</p>
              )}
            </li>
          ))}
        </ol>
      )}

      {/* 코멘트 추가 */}
      <div className="space-y-2 rounded-lg border border-dashed border-slate-200 bg-slate-50/60 p-2.5">
        <Textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={2}
          placeholder="조치 코멘트 입력…"
        />
        {err && (
          <p className="rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-xs text-rose-700">
            {err}
          </p>
        )}
        <div className="flex items-center gap-2">
          <Btn variant="ghost" onClick={() => void addComment()} disabled={busy || !comment.trim()}>
            {busy ? "추가 중…" : "코멘트 추가"}
          </Btn>
          <span className="text-[11px] text-slate-400">기록은 되돌릴 수 없습니다(append-only).</span>
        </div>
      </div>
    </Card>
  );
}

// ── 생성 폼 필드 ─────────────────────────────────────────────────────────────
function IssueFormFields({
  draft,
  onChange,
  countries,
  partners,
  contracts,
}: {
  draft: IssueDraft;
  onChange: (d: IssueDraft) => void;
  countries: Country[];
  partners: Partner[];
  contracts: ContractRow[];
}): JSX.Element {
  const set = (patch: Partial<IssueDraft>) => onChange({ ...draft, ...patch });
  const sortedCountries = useMemo(
    () =>
      [...countries].sort((a, b) =>
        (a.name_ko ?? a.name_en).localeCompare(b.name_ko ?? b.name_en, "ko"),
      ),
    [countries],
  );
  const sortedPartners = useMemo(
    () => [...partners].sort((a, b) => a.company_name.localeCompare(b.company_name, "ko")),
    [partners],
  );

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="제목 *" className="sm:col-span-2">
          <Input
            value={draft.title}
            onChange={(e) => set({ title: e.target.value })}
            placeholder="이슈 제목"
          />
        </Field>
        <Field label="유형">
          <Select
            value={draft.issue_type}
            onChange={(e) => set({ issue_type: e.target.value as DistributorIssueType })}
          >
            {TYPE_OPTS.map((t) => (
              <option key={t} value={t}>
                {TYPE_LABEL[t]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="심각도">
          <Select
            value={draft.severity}
            onChange={(e) => set({ severity: e.target.value as DistributorIssueSeverity })}
          >
            {SEVERITY_OPTS.map((s) => (
              <option key={s} value={s}>
                {SEVERITY_LABEL[s]}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="국가 (선택)">
          <Select value={draft.country_id} onChange={(e) => set({ country_id: e.target.value })}>
            <option value="">— 미지정 —</option>
            {sortedCountries.map((c) => (
              <option key={c.id} value={c.id}>
                {(c.name_ko ?? c.name_en) + " (" + c.id + ")"}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="총판 (선택)">
          <Select value={draft.partner_id} onChange={(e) => set({ partner_id: e.target.value })}>
            <option value="">— 미지정 —</option>
            {sortedPartners.map((p) => (
              <option key={p.id} value={p.id}>
                {p.company_name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="계약 (선택)">
          <Select value={draft.contract_id} onChange={(e) => set({ contract_id: e.target.value })}>
            <option value="">— 미지정 —</option>
            {contracts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name +
                  " [" +
                  (c.destination_country || "—") +
                  " · " +
                  (CONTRACT_STATUS_LABEL[c.status] ?? c.status) +
                  "]"}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="담당자 (선택)">
          <Input
            value={draft.owner}
            onChange={(e) => set({ owner: e.target.value })}
            placeholder="담당자명"
          />
        </Field>
        <Field label="기한 (선택)">
          <Input
            type="date"
            value={draft.due_date}
            onChange={(e) => set({ due_date: e.target.value })}
          />
        </Field>
      </div>

      <Field label="해결/조치 메모 (선택)">
        <Textarea
          value={draft.resolution_note}
          onChange={(e) => set({ resolution_note: e.target.value })}
          rows={2}
          placeholder="초기 메모 (선택)"
        />
      </Field>
    </div>
  );
}

// ── 헤더 ─────────────────────────────────────────────────────────────────────
function Header({
  showCreate,
  onToggle,
  disabled,
}: {
  showCreate: boolean;
  onToggle: () => void;
  disabled: boolean;
}): JSX.Element {
  return (
    <PageHeader
      eyebrow="Issues"
      title="이슈 트래킹"
      subtitle="클레임·반품·인허가 지연·독점권 분쟁 등 라이프사이클 관리 · 조치 로그 append"
      actions={
        <Btn variant="primary" onClick={onToggle} disabled={disabled}>
          {showCreate ? "닫기" : "+ 새 이슈"}
        </Btn>
      }
    />
  );
}
